import * as Sentry from '@sentry/react';
import posthog from 'posthog-js';
import React, { useContext, useEffect, useRef } from 'react';
import { shallowEqual } from 'react-redux';

import { PortalConfigContext } from 'src/context';
import { useActiveCompanyIdFromQuery } from 'src/hooks/useActiveCompanyIdFromQuery';
import { useAnonymousId } from 'src/hooks/useAnonymousId';
import { useGetUserDataQueries } from 'src/hooks/useGetUserDataQueries';
import { useAppSelector } from 'src/hooks/useStore';
import useWebSocket from 'src/hooks/useWebSocket';
import { useCustomFields } from 'src/services/api/customFieldsApi';
import { CustomFieldEntityType } from 'src/store/clients/types';
import { selectCurrentUser } from 'src/store/storeUtils';
import { initializeOnboardingTestData } from 'src/store/user/actions';
import { isLocalstackAuthEndpoint } from 'src/utils/AuthUtils';
import { getWorkspacePosthogGroupProperties } from 'src/utils/studioPosthogGroup';

export const WithUserInitialization = ({
  flexibleAuth,
  children,
}: {
  flexibleAuth: boolean;
  children: React.ReactNode;
}) => {
  // this ref is used to keep track of when the init is done.
  // In the use-effect for init side-effects we have a dependency
  // for the userData. When userData is setup we can trigger the init
  // However we don't want the side-effects to run
  // every time something on user data changes, e.g. if updatedAt
  // is modified that should not trigger the init side-effects again.
  const isUserInitialized = useRef(false);
  const { userLoaded, isClient } = useAppSelector(
    (state) => ({
      userLoaded: state.user.loaded,
      isClient: state.user.isClient,
    }),
    shallowEqual,
  );
  const userData = useAppSelector(selectCurrentUser);
  const {
    portalHeader: portalId,
    name: portalName,
    isStudioUser,
    AWS: {
      API: { endpoints },
      Auth: { endpoint: authEndpoint } = {},
    },
  } = useContext(PortalConfigContext);
  const isLocalstack = isLocalstackAuthEndpoint(authEndpoint);

  const savedAnonymousId = useAnonymousId();
  const { startWebsocket } = useWebSocket(endpoints?.at(0) ?? null);

  useGetUserDataQueries(userLoaded, flexibleAuth);
  useCustomFields(CustomFieldEntityType.CLIENT);
  useCustomFields(CustomFieldEntityType.COMPANY);

  // Parse activeCompanyId from query parameters and set in Redux store
  // This ensures client users land in the correct company context from email CTAs
  useActiveCompanyIdFromQuery();

  const userId = userData?.id;

  const isPosthogLoaded = posthog.__loaded;

  useEffect(() => {
    if (userId && portalId && portalName && isPosthogLoaded) {
      // Consolidated identify call with email (removes duplicate call from user init)
      posthog.identify(userId, {
        email: userData?.fields?.email,
      });

      // Include Studio / LocalStack cohort traits so client flag evaluation
      // matches SSR groupProperties, and so a trait the backend failed to
      // deliver at workspace creation is re-asserted on this load.
      const workspaceGroupProperties = getWorkspacePosthogGroupProperties(
        portalName,
        Boolean(isStudioUser),
        isLocalstack,
      );

      console.info(
        'posthog.group',
        'workspace',
        portalId,
        workspaceGroupProperties,
      );
      posthog.group('workspace', portalId, workspaceGroupProperties);
    }
  }, [
    userId,
    portalId,
    portalName,
    isStudioUser,
    isLocalstack,
    isPosthogLoaded,
    userData?.fields?.email,
  ]);

  useEffect(() => {
    if (userLoaded && userData?.id && !isUserInitialized.current) {
      isUserInitialized.current = true;
      const { id } = userData;
      // initialize onboarding test data
      if (!isClient) {
        initializeOnboardingTestData();
      }

      startWebsocket(id);

      // trigger identify calls
      if (analytics && analytics.identify && analytics.group) {
        if (savedAnonymousId) {
          analytics.setAnonymousId(savedAnonymousId);
        }

        analytics.identify(
          id,
          {
            name: `${userData.fields.givenName} ${userData.fields.familyName}`,
            email: userData.fields.email,
            userType: isClient ? 'client' : 'internal',
            portalURL: window.location.origin,
          },
          {
            integrations: {
              'Customer.io': !isClient,
            },
            anonymousId: savedAnonymousId,
          },
        );
        analytics.group(
          portalId,
          {
            groupType: 'Portal',
            name: portalName,
          },
          {
            integrations: {
              'Customer.io': !isClient,
              'Customer.io Actions': !isClient,
            },
          },
        );
      }

      Sentry.setUser({
        id,
        email: userData.fields.email,
        segment: portalId,
      });
    }
  }, [isClient, userLoaded, userData, savedAnonymousId]);
  return <>{children}</>;
};
